//services/supabase/process-status.ts
import { supabase } from '../../lib/supabase';
import { ProcessStatus, ProcessType } from '../../types/process';
import { triggerAnalysisSequence } from '../../utils/analysis-sequencer';

export const processStatusService = {
  async updateStatus(
    meetingId: string,
    userId: string,
    processType: ProcessType,
    status: ProcessStatus,
    errorMessage?: string
  ) {
    const now = new Date().toISOString();
    const data: any = {
      meeting_id: meetingId,
      user_id: userId,
      process_type: processType,
      status: status,
      error_message: errorMessage,
      updated_at: now
    };

    // Set start_time when process begins
    if (status === 'processing') {
      data.start_time = now;
    }

    // Set end_time and calculate duration when process completes or errors
    if (status === 'completed' || status === 'error') {
      data.end_time = now;
      if (data.start_time) {
        data.duration_ms = new Date(now).getTime() - new Date(data.start_time).getTime();
      }
    }

    const { error } = await supabase
      .from('process_statuses')
      .insert({
        ...data,
        created_at: now
      });

    if (error) throw error;

    // If transcription is complete, trigger analysis sequence
    if (processType === 'transcription' && status === 'completed') {
      try {
        const { data: transcriptionData } = await supabase
          .from('transcriptions')
          .select('transcription')
          .eq('meeting_id', meetingId)
          .single();

        if (transcriptionData?.transcription) {
          await this.updateStatus(meetingId, userId, 'analysis', 'processing');
          await triggerAnalysisSequence(transcriptionData.transcription, meetingId, userId);
        }
      } catch (error) {
        console.error('Error in transcription completion handler:', error);
        await this.updateStatus(
          meetingId,
          userId,
          'analysis',
          'error',
          error instanceof Error ? error.message : 'Unknown error'
        );
      }
    }
  },

  subscribeToTranscriptionComplete(meetingId: string, userId: string) {
    console.log('Initializing transcription subscription...');
    
    const subscription = supabase
      .channel(`transcription-${meetingId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'process_statuses',
          filter: `meeting_id=eq.${meetingId}`,
        },
        async (payload) => {
          console.log('Process status change detected:', payload);
          
          if (
            payload.new.process_type === 'transcription' &&
            payload.new.status === 'completed'
          ) {
            console.log('Transcription completed, fetching transcript...');
            
            try {
              const { data: transcriptionData } = await supabase
                .from('transcriptions')
                .select('transcription')
                .eq('meeting_id', meetingId)
                .single();

              if (transcriptionData?.transcription) {
                console.log('Starting analysis sequence...');
                await processStatusService.updateStatus(
                  meetingId,
                  userId,
                  'analysis',
                  'processing'
                );

                await triggerAnalysisSequence(
                  transcriptionData.transcription,
                  meetingId,
                  userId
                );
              }
            } catch (error) {
              console.error('Error in transcription completion handler:', error);
              await processStatusService.updateStatus(
                meetingId,
                userId,
                'analysis',
                'error',
                error instanceof Error ? error.message : 'Unknown error'
              );
            }
          }
        }
      )
      .subscribe((status) => {
        console.log('Transcription subscription initialized', status);
      });

    return subscription;
  }
};